// src/utils/audio/wavEncoder.ts

/**
 * 16-bit PCM WAV encoding for the consultation capture pipeline.
 *
 * The capture service used to hand-encode IEEE float WAV (format tag 3, 4 bytes/sample) at the
 * native AudioContext rate (~48kHz), which is ~192 kB/s on the wire. transcribe's Whisper
 * pipeline resamples everything to 16kHz mono anyway, so the extra rate and the extra two bytes
 * per sample are thrown away downstream — 10s of speech is ~320KB here against ~1.9MB before.
 *
 * The sample rate is written into the header rather than assumed, so a browser that refuses the
 * 16kHz AudioContext hint still produces a self-describing packet that transcribe reads with
 * soundfile and resamples itself.
 */

const WAV_HEADER_BYTES = 44;
const BYTES_PER_SAMPLE = 2;
const NUM_CHANNELS = 1;
const PCM_FORMAT_TAG = 1; // 1 = integer PCM (3 = IEEE float, what this replaced)

const writeString = (view: DataView, offset: number, str: string) => {
	for (let i = 0; i < str.length; i++) {
		view.setUint8(offset + i, str.charCodeAt(i));
	}
};

/**
 * Encode mono Float32 samples as a 16-bit PCM WAV, returning the raw bytes.
 * Samples are clamped to [-1, 1] before scaling — an out-of-range value would otherwise wrap
 * around setInt16 and land as loud noise of the opposite sign.
 */
export const encodeWavInt16Buffer = (samples: Float32Array, sampleRate: number): ArrayBuffer => {
	const blockAlign = NUM_CHANNELS * BYTES_PER_SAMPLE;
	const dataBytes = samples.length * BYTES_PER_SAMPLE;
	const buffer = new ArrayBuffer(WAV_HEADER_BYTES + dataBytes);
	const view = new DataView(buffer);

	writeString(view, 0, "RIFF");
	view.setUint32(4, 36 + dataBytes, true);
	writeString(view, 8, "WAVE");
	writeString(view, 12, "fmt ");
	view.setUint32(16, 16, true);
	view.setUint16(20, PCM_FORMAT_TAG, true);
	view.setUint16(22, NUM_CHANNELS, true);
	view.setUint32(24, sampleRate, true);
	view.setUint32(28, sampleRate * blockAlign, true);
	view.setUint16(32, blockAlign, true);
	view.setUint16(34, BYTES_PER_SAMPLE * 8, true);
	writeString(view, 36, "data");
	view.setUint32(40, dataBytes, true);

	let offset = WAV_HEADER_BYTES;
	for (let i = 0; i < samples.length; i++, offset += BYTES_PER_SAMPLE) {
		const clamped = Math.max(-1, Math.min(1, samples[i]));
		view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
	}

	return buffer;
};

/** Blob wrapper for the encoder — what the streaming service hands to FileReader. */
export const encodeWavInt16 = (samples: Float32Array, sampleRate: number): Blob =>
	new Blob([encodeWavInt16Buffer(samples, sampleRate)], { type: "audio/wav" });
